import { MutationCache, onlineManager, QueryCache, QueryClient } from "@tanstack/react-query"
import { Refused } from "@/administration/refused.ts"

/** How many times a call that failed before the web API could answer is made again. */
const retriesAfterAFault = 2

/**
 * Whether to ask again after a failed call. A refusal is the web API's decision, so asking again
 * can only get the same answer; a failure that never reached it, such as a dropped connection, may
 * pass on a second try.
 */
export function shouldRetry(failureCount: number, error: unknown) {
  return !(error instanceof Refused) && failureCount < retriesAfterAFault
}

/** Whether a call failed because the web API found no session: it lapsed, or was ended. */
const signedOut = (error: unknown) => error instanceof Refused && error.refusal === "not_signed_in"

/**
 * The web app's one cache of what the web API answered. A query is asked again whenever the window
 * regains focus and whenever a page that shows it mounts, so what a Member sees is never older than
 * their last look away; a page that must stay fresh while open polls on top of that. While the
 * browser is offline a query waits rather than failing, and is asked as the network returns.
 * Any call the web API refuses for want of a session (a lapsed one, or a Member deactivated since)
 * tells `onSignedOut`, so the app forgets what it kept of them on the device.
 */
export function createQueryClient({ onSignedOut }: { onSignedOut?: () => void } = {}) {
  // The cache takes itself to be online until the browser says otherwise, which a page opened
  // offline (the installed app's shell, from its service worker) never hears it say.
  onlineManager.setOnline(navigator.onLine)
  const told = (error: unknown) => {
    if (signedOut(error)) onSignedOut?.()
  }
  return new QueryClient({
    queryCache: new QueryCache({ onError: told }),
    mutationCache: new MutationCache({ onError: told }),
    defaultOptions: {
      queries: { retry: shouldRetry, refetchOnWindowFocus: true },
      mutations: { retry: false },
    },
  })
}
